import { SelectIcon } from "@common/SelectIcon/SelectIcon";
import { createTheme, PaletteColor, PaletteColorOptions } from "@mui/material/styles";
import { CSSProperties } from "react";
import { WHITE_LABEL_COLORS } from "./colors";
import { FONT_WEIGHTS } from "./fontWeights";
import { createTypography } from "./typography";

// Used to add some color properties
declare module "@mui/material/styles" {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypeBackground {
        main?: string;
        dark?: string;
        light?: string;
        eggShell?: string;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Palette {
        tertiary: PaletteColor;
        quaternary: PaletteColor;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface PaletteOptions {
        tertiary?: PaletteColorOptions;
        quaternary?: PaletteColorOptions;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypographyVariants {
        large: CSSProperties;
        normal: CSSProperties;
        small: CSSProperties;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypographyVariantsOptions {
        large?: CSSProperties;
        normal?: CSSProperties;
        small?: CSSProperties;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Typography {
        fontWeight: typeof FONT_WEIGHTS;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypographyOptions {
        fontWeight?: typeof FONT_WEIGHTS;
    }
}

declare module "@mui/material/Typography" {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface TypographyPropsVariantOverrides {
        large: true;
        normal: true;
        small: true;
    }
}

type CustomTypographyVariant = "large" | "normal" | "small";

export type PaletteColorKeys = "primary" | "secondary" | "tertiary" | "quaternary" | "error" | "success";

const VARIANT_MAPPING: Record<CustomTypographyVariant, string> = {
    large: "p",
    normal: "p",
    small: "p",
};

const breakpointsTheme = createTheme();

export const muiTheme = createTheme({
    palette: {
        primary: {
            main: WHITE_LABEL_COLORS.primary,
            contrastText: WHITE_LABEL_COLORS.white,
        },
        secondary: {
            main: WHITE_LABEL_COLORS.secondary,
            contrastText: WHITE_LABEL_COLORS.white,
        },
        tertiary: {
            main: WHITE_LABEL_COLORS.tertiary,
            contrastText: WHITE_LABEL_COLORS.white,
        },
        quaternary: {
            main: WHITE_LABEL_COLORS.quaternary,
            contrastText: WHITE_LABEL_COLORS.secondary,
        },
        error: {
            main: WHITE_LABEL_COLORS.error,
        },
        background: {
            main: WHITE_LABEL_COLORS.neutral1,
            dark: WHITE_LABEL_COLORS.neutral2,
            light: WHITE_LABEL_COLORS.white,
            eggShell: WHITE_LABEL_COLORS.eggShell,
        },
    },
    shape: {
        borderRadius: 12,
    },
    typography: createTypography(breakpointsTheme.breakpoints),
    components: {
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    "&.Mui-focusVisible": {
                        outline: "auto 5px -webkit-focus-ring-color !important",
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                rounded: {
                    borderRadius: "10px",
                },
                elevation2: {
                    boxShadow:
                        "0px 0px 8px 0px rgba(0, 0, 0, 0)," +
                        "0px 0px 4px 0px rgba(0, 0, 0, 0), " +
                        "0px 0px 7px 2px rgba(0, 0, 0, 0.11);",
                },
            },
        },
        MuiFab: {
            styleOverrides: {
                root: {
                    boxShadow: "none",
                },
                secondary: ({ theme }) => ({
                    color: theme.palette.tertiary.main,
                }),
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "initial",
                    borderRadius: "30px",
                },
                contained: {
                    boxShadow: "none",
                },
                outlined: {
                    boxShadow: "none",
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                content: {
                    zIndex: 10,
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    overflow: "hidden",
                    "&:last-child": {
                        paddingBottom: "16px",
                    },
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                IconComponent: SelectIcon,
                MenuProps: {
                    disableAutoFocusItem: true,
                },
            },
            styleOverrides: {
                root: {
                    variants: [
                        {
                            props: { variant: "filled" },
                            style: {
                                borderRadius: "12px",
                            },
                        },
                        {
                            props: { variant: "outlined" },
                            style: {
                                paddingRight: "10px",
                            },
                        },
                    ],
                },
                filled: ({ theme }) => ({
                    borderRadius: "12px",
                    backgroundColor: "white",
                    paddingLeft: "10px",
                    paddingRight: "45px !important",
                    transition: "0.20s",
                    textAlign: "left",
                    border: `1px solid ${theme.palette.secondary.main}`,
                    "&[aria-expanded='true']": {
                        borderRadius: `12px 12px 0 0`,
                    },
                    "&.Mui-error": {
                        border: `1px solid ${theme.palette.error.main}`,
                        "&[aria-expanded='true']": {
                            borderColor: theme.palette.secondary.main,
                            borderRadius: `12px 12px 0 0`,
                        },
                    },
                    "&:focus": {
                        border: `1px solid ${theme.palette.secondary.main}`,
                        boxShadow: `0 0 0 1px ${theme.palette.secondary.main}`,
                        borderRadius: `12px`,
                    },
                    "&:hover": {
                        boxShadow: `0 0 0 0`,
                        border: `1px solid ${theme.palette.primary.main}`,
                    },
                }),
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: ({ theme }) => ({
                    boxShadow: "none",
                    boxSizing: "border-box",
                    borderRadius: `0 0 12px 12px`,
                    borderLeft: `1px solid ${theme.palette.secondary.main}`,
                    borderRight: `1px solid ${theme.palette.secondary.main}`,
                    borderBottom: `1px solid ${theme.palette.secondary.main}`,
                }),
            },
        },
        MuiMobileStepper: {
            styleOverrides: {
                root: {
                    backgroundColor: "transparent",
                    justifyContent: "center",
                },
                dotActive: ({ theme }) => ({
                    backgroundColor: theme.palette.secondary.main,
                }),
            },
        },
        MuiInput: {
            styleOverrides: {
                underline: {
                    "&&&&:hover:before": {
                        borderBottom: "1px solid",
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.secondary.main,
                }),
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                shrink: {
                    transform: "translate(0, 1.5px) scale(0.95)",
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: ({ theme }) => ({
                    fontWeight: 100,
                    "&.Mui-selected": {
                        backgroundColor: theme.palette.background.dark,
                    },
                    "&:hover": {
                        backgroundColor: theme.palette.background.main,
                    },
                }),
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    backgroundColor: "transparent",
                },
                input: {
                    padding: "6px 0 7px",
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                colorPrimary: ({ theme }) => ({
                    backgroundColor: theme.palette.secondary.contrastText,
                }),
                barColorPrimary: ({ theme }) => ({
                    backgroundColor: theme.palette.secondary.main,
                }),
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: ({ theme }) => ({
                    height: "72px",
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.contrastText,
                }),
                scroller: {
                    marginBottom: "0px !important",
                },
                scrollButtons: {
                    height: "72px",
                },
                indicator: ({ theme }) => ({
                    height: "4px",
                    backgroundColor: theme.palette.quaternary.main,
                }),
            },
        },
        MuiTab: {
            styleOverrides: {
                root: ({ theme }) => ({
                    height: "72px",
                    textTransform: "initial",
                    fontSize: "17px !important",
                    "&:hover": {
                        color: theme.palette.quaternary.main,
                    },
                    "&.Mui-selected": {
                        color: theme.palette.quaternary.main,
                    },
                }),
                textColorInherit: {
                    opacity: 1,
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: "0.7em",
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                body: {
                    fontSize: "inherit",
                },
                head: {
                    fontSize: "inherit",
                },
            },
        },
        // @ts-expect-error: Unreachable code error
        MuiPickersDay: {
            styleOverrides: {
                day: {
                    fontSize: "0.9em",
                },
            },
        },
        MuiTypography: {
            defaultProps: {
                variantMapping: VARIANT_MAPPING,
            },
            styleOverrides: {
                root: ({ ownerState, theme }) => {
                    const variantStyles = theme.typography[ownerState.variant as CustomTypographyVariant];

                    return {
                        fontFamily: (variantStyles?.fontFamily as string) ?? theme.typography.fontFamily,
                    };
                },
            },
        },
    },
});
